import React, { useEffect } from 'react';
import { Box, Button, Chip, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import dayjs from 'dayjs';
import Topbar from '../../components/topbar';
import DynamicAdminTable, { Column } from '../../components/admin-table';
import { useToast } from '../../providers/toaster-provider';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';

type Reimbursement = {
    id: number;
    entityId: number;
    entityName: string;
    fromTierName: string;
    toTierName: string;
    // Set for an add-on turned on mid-period (charged the full period), not a plan change.
    addonLabel?: string | null;
    amount: number;
    status: 'pending' | 'approved' | 'rejected';
    stripeRefundId: string | null;
    // False only for a record that predates refund tracking, or where
    // Stripe never returned a PaymentIntent - approving one of these is
    // refused server-side rather than silently skipping the refund.
    canRefund: boolean;
    createdAt: string;
};

// A subscription payment or refund as recorded in the company's own
// Transactions (subscription-invoice-service.js).
type SubscriptionTransaction = {
    guid: string;
    entityId: number;
    entityName: string;
    description: string;
    amount: number;
    date: string;
    type: 'payment' | 'refund';
    invoiceNumber: string | null;
};

type RowType = 'payment' | 'refund' | 'reimbursement';
type Filter = 'all' | RowType;

const formatMoney = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_COLOR: Record<string, string> = {
    pending: 'var(--color-text-muted)',
    approved: '#1a7f37',
    rejected: '#c00',
};

const TYPE_LABEL: Record<RowType, string> = {
    payment: 'Payment',
    refund: 'Refund',
    reimbursement: 'Reimbursement',
};

const TYPE_COLOR: Record<RowType, string> = {
    payment: 'var(--color-primary)',
    refund: '#1a7f37',
    reimbursement: '#b26a00',
};

// Every company's subscription transactions in one place - payments and
// refunds, plus the reimbursement queue (see changePlan/subscription-stripe-
// service.js and subscription-addons-service.js: a plan change or an add-on
// turned on mid-period is billed in full, and the suggested credit lands
// here for someone to action). Approving a reimbursement is the
// human-in-the-loop gate AND actually issues the real Stripe refund at that
// moment (subscription-reimbursement-controller.js) - rejecting is a pure
// record-only no-op, nothing is ever charged or refunded on a rejection.
const SubscriptionReimbursementsAdmin: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const [fetchApi, setFetchApi] = useApi<{ data: Reimbursement[] }>();
    const [txApi, setTxApi] = useApi<{ data: SubscriptionTransaction[] }>();
    const [resolveApi, setResolveApi] = useApi<{ status: string; stripeRefundId: string | null }>();
    const [filter, setFilter] = React.useState<Filter>('all');

    useEffect(() => { document.title = 'FineAnts Admin | Subscription Transactions'; }, []);

    const refetch = () => {
        setFetchApi(buildApiRequestConfig(routes.subscriptionReimbursements.list));
        setTxApi(buildApiRequestConfig(routes.subscriptionReimbursements.transactions));
    };
    useEffect(() => { refetch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    useEffect(() => {
        const { data, isLoading, error } = resolveApi;
        if (data && !isLoading) {
            showSuccess(data.stripeRefundId ? `Refund issued (${data.stripeRefundId})` : 'Marked as rejected - no refund issued');
            refetch();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to save');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolveApi]);

    const handleResolve = (id: number, status: 'approved' | 'rejected') => {
        const { url, method } = buildApiRequestConfig(routes.subscriptionReimbursements.resolve, [{ param: 'id', value: id }]);
        setResolveApi({ url, method, body: { status } });
    };

    const typeChip = (type: RowType) => (
        <Chip
            label={TYPE_LABEL[type]}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700, color: TYPE_COLOR[type], borderColor: TYPE_COLOR[type] }}
        />
    );

    const reimbursementRows = (fetchApi.data?.data ?? []).map(r => ({
        key: `r-${r.id}`,
        type: 'reimbursement' as RowType,
        sortDate: r.createdAt,
        entityName: r.entityName,
        typeDisplay: typeChip('reimbursement'),
        // An add-on turned on mid-period (charged the full period) rather
        // than a plan change.
        description: r.addonLabel
            ? `${r.fromTierName} plan: ${r.addonLabel} add-on - suggested refund`
            : `${r.fromTierName} → ${r.toTierName} - suggested refund`,
        amountDisplay: formatMoney(r.amount),
        dateDisplay: dayjs(r.createdAt).format('DD MMM YYYY, h:mm A'),
        statusDisplay: (
            <Box display="flex" alignItems="center" gap={0.75}>
                <Chip
                    label={r.status}
                    size="small"
                    sx={{ textTransform: 'capitalize', fontWeight: 700, color: '#fff', backgroundColor: STATUS_COLOR[r.status] }}
                />
                {r.status === 'pending' && !r.canRefund && (
                    <Tooltip title="No payment on file for this record - approving will be refused. Refund it manually instead.">
                        <AlertTriangle size={14} color="#c00" />
                    </Tooltip>
                )}
            </Box>
        ),
        actionsDisplay: r.status === 'pending' ? (
            <Box display="flex" gap={1} justifyContent="flex-end">
                <Button
                    size="small"
                    variant="contained"
                    disabled={resolveApi.isLoading}
                    onClick={() => handleResolve(r.id, 'approved')}
                    sx={{ textTransform: 'none', boxShadow: 'none', whiteSpace: 'nowrap' }}
                >
                    Approve & Refund
                </Button>
                <Button
                    size="small"
                    color="error"
                    disabled={resolveApi.isLoading}
                    onClick={() => handleResolve(r.id, 'rejected')}
                    sx={{ textTransform: 'none' }}
                >
                    Reject
                </Button>
            </Box>
        ) : null,
    }));

    const transactionRows = (txApi.data?.data ?? []).map(t => ({
        key: `t-${t.guid}`,
        type: t.type as RowType,
        sortDate: t.date,
        entityName: t.entityName,
        typeDisplay: typeChip(t.type),
        description: t.invoiceNumber ? `${t.description} (${t.invoiceNumber})` : t.description,
        // Recorded negative in the company's books (money leaving them) -
        // shown here as what FineAnts received or paid back.
        amountDisplay: formatMoney(Math.abs(t.amount)),
        dateDisplay: dayjs(t.date).format('DD MMM YYYY, h:mm A'),
        statusDisplay: null,
        actionsDisplay: null,
    }));

    const allRows = [...reimbursementRows, ...transactionRows]
        .sort((a, b) => dayjs(b.sortDate).valueOf() - dayjs(a.sortDate).valueOf());
    const counts: Record<Filter, number> = {
        all: allRows.length,
        payment: allRows.filter(r => r.type === 'payment').length,
        refund: allRows.filter(r => r.type === 'refund').length,
        reimbursement: allRows.filter(r => r.type === 'reimbursement').length,
    };
    const pendingCount = (fetchApi.data?.data ?? []).filter(r => r.status === 'pending').length;
    const rows = filter === 'all' ? allRows : allRows.filter(r => r.type === filter);

    const columns: Column[] = [
        { id: 'dateDisplay', label: 'Date', size: 160, disabled: true },
        { id: 'entityName', label: 'Entity', size: 180, disabled: true },
        { id: 'typeDisplay', label: 'Type', size: 140, disabled: true },
        { id: 'description', label: 'Description', size: 320, disabled: true },
        { id: 'amountDisplay', label: 'Amount', size: 120, disabled: true },
        { id: 'statusDisplay', label: 'Status', size: 130, disabled: true },
        { id: 'actionsDisplay', label: '', size: 200, disabled: true },
    ];

    return (
        <div style={{ width: '100%' }}>
            <Topbar title="Subscription Transactions" />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={filter}
                        onChange={(_, value: Filter | null) => value && setFilter(value)}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75 } }}
                    >
                        <ToggleButton value="all">All ({counts.all})</ToggleButton>
                        <ToggleButton value="payment">Payments ({counts.payment})</ToggleButton>
                        <ToggleButton value="refund">Refunds ({counts.refund})</ToggleButton>
                        <ToggleButton value="reimbursement">
                            Reimbursements ({counts.reimbursement}){pendingCount > 0 ? ` - ${pendingCount} pending` : ''}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Box sx={{ fontSize: 13, color: 'var(--color-text-muted)', maxWidth: 560 }}>
                        Approving a reimbursement issues a real Stripe refund immediately for the suggested amount. Rejecting just records the decision - nothing is charged or refunded.
                    </Box>
                </Box>
                <DynamicAdminTable columns={columns} data={rows} />
            </div>
        </div>
    );
};

export default SubscriptionReimbursementsAdmin;
